import React, { useState } from "react";
import { Card, Form, Input, Button, Typography, message, Switch } from "antd";
import { LockOutlined, BulbOutlined } from "@ant-design/icons";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useTheme } from "Common/hooks/context/ThemeProvider";
import { toast } from "react-toastify";

const { Title, Text } = Typography;

const PlatformInvite = () => {
  const [loading, setLoading] = useState(false);
  const { darkMode, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(window.location.search);
  const urlToken = queryParams.get('token');

  const handleRegister = async (values) => {
    if (!urlToken) {
      message.error("Registration token is missing");
      return;
    }

    setLoading(true);
    const BASE_URL = process.env.REACT_APP_API_URL;

    try {
      await axios.post(`${BASE_URL}/TenantUserInvites/Accept-Invite`, {
        tenantToken: urlToken,
        password: values.password,
        firstName: values.firstName,
        lastName: values.lastName
      });
      message.success("Registration successful!");
      navigate("/login");
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Registration failed";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ ...styles.container, backgroundColor: darkMode ? "#1e1e2f" : "#f4f6f9" }}>
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Card
          style={{
            ...styles.card,
            backgroundColor: darkMode ? "#2d2d3a" : "#fff",
            color: darkMode ? "#fff" : "#000",
          }}
          bordered={false}
        >
          <div style={styles.header}>
            <Title level={3} style={{ color: darkMode ? "#fff" : "#1890ff" }}>
              Create an Account
            </Title>
            <div style={styles.themeToggle}>
              <BulbOutlined style={{ marginRight: 8 }} />
              <Switch checked={darkMode} onChange={toggleTheme} />
            </div>
          </div>

          <Text type="secondary" style={{ display: "block", marginBottom: 24, color: darkMode ? "#ccc" : undefined }}>
            Please fill in the details to register
          </Text>

          <Form name="register-form" layout="vertical" onFinish={handleRegister}>
            <Form.Item
              name="firstName"
              label={<span style={{ color: darkMode ? "#fff" : "#000" }}>First Name</span>}
              rules={[{ required: true, message: "Please enter your first name!" }]}
            >
              <Input placeholder="John" />
            </Form.Item>

            <Form.Item
              name="lastName"
              label={<span style={{ color: darkMode ? "#fff" : "#000" }}>Last Name</span>}
              rules={[{ required: true, message: "Please enter your last name!" }]}
            >
              <Input placeholder="Doe" />
            </Form.Item>

            <Form.Item
              name="password"
              label={<span style={{ color: darkMode ? "#fff" : "#000" }}>Password</span>}
              rules={[
                { required: true, message: "Please enter your password!" },
                { min: 8, message: "Password must be at least 8 characters!" },
              ]}
            >
              <Input.Password  prefix={<LockOutlined />} placeholder="Enter your password" />
            </Form.Item>

            <Form.Item 
              name="confirmPassword"
              label={<span style={{ color: darkMode ? "#fff" : "#000" }}>Confirm Password</span>}
              dependencies={['password']}
              rules={[
                { required: true, message: 'Please confirm your password!' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Passwords do not match!'));
                  },
                }),
              ]}
            >
              <Input.Password prefix={<LockOutlined />} placeholder="Confirm Your Password" />
            </Form.Item>

            <Form.Item>
              <motion.div whileTap={{ scale: 0.95 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  block
                  style={{
                    backgroundColor: darkMode ? "#226c87c7" : "#0c313f",
                    borderColor: darkMode ? "#226c87c7" : "#0c313f",
                    color: "#fff",
                  }}
                >
                  {loading ? "Registering..." : "Register"}
                </Button>
              </motion.div>
            </Form.Item>
          </Form>

          <Text type="secondary" style={{ color: darkMode ? "#aaa" : undefined }}>
            Already have an account?{" "}
            <Button type="link" onClick={() => navigate("/login")}>
              Log In
            </Button>
          </Text>
        </Card>
      </motion.div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "20px",
    transition: "background-color 0.3s ease",
  },
  card: {
    width: "700px",
    padding: "40px",
    borderRadius: "12px",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.1)",
    transition: "background-color 0.3s ease, color 0.3s ease",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  themeToggle: {
    display: "flex",
    alignItems: "center",
  },
};

export default PlatformInvite;